/**
 * Owner: Masa Bayu
 * File: my-books-projection.ts
 * Date:Time: 2026-09-26 22:35
 * Description: Same-origin Writer shelf projection + local delete for My Books.
 */

const SHELF_KEY = 'qxwriter.book-shelf';
const PUB_PREFIX = 'qxwriter.publication-status:';
const OPEN_BOOK_KEY = 'qxwriter.open-book-id';
const PLACE_KEY = 'qxwriter.library-place';
const RECENT_KEY = 'qxwriter.book-recent';
const GENERATION_KEY = 'qxwriter.book-generation';
const LIVE_CONTEXT_KEY = 'qxwriter.book-context';
const LIVE_COVER_KEY = 'qxwriter.cover-preview';

function bookBodyKey(id: string): string {
  return `qxwriter.book:${id}`;
}

function bookContextKey(id: string): string {
  return `qxwriter.book-context:${id}`;
}

function bookCoverKey(id: string): string {
  return `qxwriter.cover-preview:${id}`;
}

export type MyBookStatusKind = 'unpublished' | 'published' | 'changed';

export type MyBookCard = {
  readonly id: string;
  readonly title: string;
  readonly updatedAt: string;
  readonly updatedLabel: string;
  readonly statusKind: MyBookStatusKind;
  readonly statusLabel: string | null;
  readonly publicationId: string | null;
};

type ShelfEntry = {
  readonly id: string;
  readonly title: string;
  readonly updatedAt: string;
};

type PubStored = {
  readonly publishedAt: string;
  readonly fingerprint: string;
};

function canUseStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function fingerprint(bookId: string, title: string, updatedAt: string): string {
  return `${bookId}|${title.trim()}|${updatedAt}`;
}

function readPubRecord(bookId: string): PubStored | null {
  if (!canUseStorage() || !bookId) return null;
  try {
    const raw = window.localStorage.getItem(`${PUB_PREFIX}${bookId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PubStored>;
    if (typeof parsed.publishedAt !== 'string' || typeof parsed.fingerprint !== 'string') {
      return null;
    }
    return { publishedAt: parsed.publishedAt, fingerprint: parsed.fingerprint };
  } catch {
    return null;
  }
}

function statusFor(entry: ShelfEntry): {
  readonly kind: MyBookStatusKind;
  readonly label: string | null;
} {
  const stored = readPubRecord(entry.id);
  if (!stored) return { kind: 'unpublished', label: null };
  const current = fingerprint(entry.id, entry.title, entry.updatedAt);
  if (stored.fingerprint === current) {
    return { kind: 'published', label: 'Published' };
  }
  return { kind: 'changed', label: 'Changes not published' };
}

function updatedLabel(updatedAt: string, now = Date.now()): string {
  const then = Date.parse(updatedAt);
  if (Number.isNaN(then)) return '';
  if (now - then < 24 * 60 * 60 * 1000) return 'Updated recently';
  return new Date(then).toLocaleDateString();
}

function readShelfEntries(): readonly ShelfEntry[] {
  if (!canUseStorage()) return [];
  try {
    const raw = window.localStorage.getItem(SHELF_KEY);
    if (raw === null || raw.trim().length === 0) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry): entry is ShelfEntry => {
      if (!entry || typeof entry !== 'object') return false;
      const book = entry as ShelfEntry;
      return (
        typeof book.id === 'string' &&
        typeof book.title === 'string' &&
        typeof book.updatedAt === 'string'
      );
    });
  } catch {
    return [];
  }
}

function normalizeTitle(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Match host catalog by title only when unique — never invent a URL. */
export function matchCatalogPublicationId(
  title: string,
  catalog: readonly { readonly publicationId: string; readonly title: string }[],
): string | null {
  const want = normalizeTitle(title);
  if (!want) return null;
  const hits = catalog.filter((entry) => normalizeTitle(entry.title) === want);
  if (hits.length !== 1) return null;
  return hits[0]!.publicationId;
}

export function projectMyBooks(input?: {
  readonly catalog?: readonly { readonly publicationId: string; readonly title: string }[];
  readonly now?: number;
}): readonly MyBookCard[] {
  const catalog = input?.catalog ?? [];
  const now = input?.now ?? Date.now();
  return readShelfEntries().map((entry) => {
    const status = statusFor(entry);
    return {
      id: entry.id,
      title: entry.title.trim() || 'Untitled Book',
      updatedAt: entry.updatedAt,
      updatedLabel: updatedLabel(entry.updatedAt, now),
      statusKind: status.kind,
      statusLabel: status.label,
      publicationId: matchCatalogPublicationId(entry.title, catalog),
    };
  });
}

/** Hand off a shelf book id to QXWriter on the same origin. */
export function handoffOpenWriterBook(bookId: string): void {
  if (!canUseStorage() || !bookId) return;
  try {
    window.localStorage.setItem(OPEN_BOOK_KEY, bookId);
    window.localStorage.setItem(PLACE_KEY, 'writer');
  } catch {
    /* ignore */
  }
}

export function handoffOpenWriterLibrary(): void {
  if (!canUseStorage()) return;
  try {
    window.localStorage.removeItem(OPEN_BOOK_KEY);
    window.localStorage.removeItem('qxwriter.open-profile');
    window.localStorage.setItem(PLACE_KEY, 'library');
  } catch {
    /* ignore */
  }
}

function readRecentBookIds(): readonly string[] {
  if (!canUseStorage()) return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(RECENT_KEY) ?? '[]') as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === 'string');
  } catch {
    return [];
  }
}

/** Best-effort: live Writer body matches this shelf id (no QXW import). */
function liveGenerationMatches(bookId: string): boolean {
  if (!canUseStorage() || !bookId) return false;
  try {
    const raw = window.localStorage.getItem(GENERATION_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { format?: unknown; content?: unknown };
    if (parsed.format !== 'qx-book-generation-v1' || typeof parsed.content !== 'string') {
      return false;
    }
    return new RegExp(`"id"\\s*:\\s*"${bookId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"`).test(
      parsed.content,
    );
  } catch {
    return false;
  }
}

/**
 * Delete local book on this device — same Writer shelf keys.
 * Does not unpublish a catalog listing (Delete local ≠ Unpublish).
 */
export function deleteLocalMyBook(bookId: string): boolean {
  if (!canUseStorage() || !bookId) return false;
  try {
    window.localStorage.removeItem(bookBodyKey(bookId));
    window.localStorage.removeItem(bookContextKey(bookId));
    window.localStorage.removeItem(bookCoverKey(bookId));
    window.localStorage.removeItem(`${PUB_PREFIX}${bookId}`);
    const rest = readShelfEntries().filter((book) => book.id !== bookId);
    window.localStorage.setItem(SHELF_KEY, JSON.stringify(rest));
    window.localStorage.setItem(
      RECENT_KEY,
      JSON.stringify(readRecentBookIds().filter((id) => id !== bookId)),
    );
    if (window.localStorage.getItem(OPEN_BOOK_KEY) === bookId) {
      window.localStorage.removeItem(OPEN_BOOK_KEY);
      window.localStorage.setItem(PLACE_KEY, 'library');
    }
    if (liveGenerationMatches(bookId)) {
      window.localStorage.removeItem(GENERATION_KEY);
      window.localStorage.removeItem(LIVE_CONTEXT_KEY);
      window.localStorage.removeItem(LIVE_COVER_KEY);
      window.localStorage.setItem(PLACE_KEY, 'library');
    }
    return true;
  } catch {
    return false;
  }
}

export const MY_BOOKS_OPEN_BOOK_KEY = OPEN_BOOK_KEY;
